import type { SlotRenderHooks } from '@zag/calendar/models';

/**
 * Resolver returning a localised label for an absence-type key.
 * Receives the raw `data.absenceType` value (with or without `absence:` prefix)
 * and returns the localised label or `undefined` when the key is unknown.
 */
export type AbsenceTypeLabelResolver = (typeKey: string) => string | undefined;

const ABSENCE_TYPE_FIELD = 'absenceType';
const ABSENCE_TYPE_PREFIX = 'absence:';
const ABSENCE_DATA_TYPE = 'absence';
export const ABSENCE_FALLBACK_ICON = 'event_busy';
export const ABSENCE_FALLBACK_COLOR = '#f97316';

export function isAbsenceSlotData(raw: Record<string, unknown>): boolean {
  const type = raw['type'];
  return typeof type === 'string' && (type === ABSENCE_DATA_TYPE || type.startsWith(ABSENCE_TYPE_PREFIX));
}

export function deriveAbsenceTypeKey(raw: Record<string, unknown>): string | undefined {
  const rawType = raw[ABSENCE_TYPE_FIELD];
  if (typeof rawType === 'string' && rawType.length > 0) {
    return rawType.startsWith(ABSENCE_TYPE_PREFIX) ? rawType : `${ABSENCE_TYPE_PREFIX}${rawType}`;
  }

  const slotType = raw['type'];
  if (typeof slotType === 'string' && slotType.startsWith(ABSENCE_TYPE_PREFIX)) {
    return slotType;
  }

  return undefined;
}

export function absenceSlugOf(typeKey: string): string {
  return typeKey.startsWith(ABSENCE_TYPE_PREFIX) ? typeKey.slice(ABSENCE_TYPE_PREFIX.length) : typeKey;
}

export function deriveAbsenceSlotTitle(raw: Record<string, unknown>, labelResolver?: AbsenceTypeLabelResolver): string | undefined {
  const prefixed = deriveAbsenceTypeKey(raw);
  if (!prefixed) return undefined;
  const stripped = absenceSlugOf(prefixed);

  const fromResolver = labelResolver?.(prefixed) ?? labelResolver?.(stripped);
  if (fromResolver && fromResolver.length > 0) return fromResolver;

  if (stripped.length === 0) return undefined;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

export function createAbsenceSlotRenderHooks(labelResolver?: AbsenceTypeLabelResolver): SlotRenderHooks {
  return {
    resolveTitle: (raw: Record<string, unknown>) => (isAbsenceSlotData(raw) ? deriveAbsenceSlotTitle(raw, labelResolver) : undefined),
    resolveIcon: (raw: Record<string, unknown>) => (isAbsenceSlotData(raw) ? ABSENCE_FALLBACK_ICON : undefined),
    resolveColor: (raw: Record<string, unknown>) => (isAbsenceSlotData(raw) ? ABSENCE_FALLBACK_COLOR : undefined),
    resolveIsAllDay: (raw: Record<string, unknown>) =>
      isAbsenceSlotData(raw) ? raw['portion'] === undefined || raw['portion'] === null || raw['portion'] === 'full' : undefined,
    isEditableOverride: (raw: Record<string, unknown>) => isAbsenceSlotData(raw),
    resolveHiddenFormFields: (slotTypeKey: string, category?: string) =>
      slotTypeKey === ABSENCE_DATA_TYPE || category === ABSENCE_DATA_TYPE ? ABSENCE_HIDDEN_FORM_FIELDS : undefined,
  };
}

const ABSENCE_HIDDEN_FORM_FIELDS: ReadonlyArray<string> = ['title', 'description', 'locationOverride'];
